import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  PODS_DATA_ATTENDANCE,
  PROJECTS_BY_EMP_ID,
  SCREENS_BY_PROJECT,
  POD_GOAL_TEMPLATES,
  EMPLOYEES_BY_AGENCY_ID,
} from "../../api/query";
import { CREATE_POD_GOAL_CUSTOM } from "../../api/mutation";
import BreadCumb from "../../components/common/Breadcrumb";
import Loader from "../../components/common/Loader";
import DateSelector from "../../components/common/DateSelector";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import backArrow from "../../assets/task/back_arrow.svg";
import { getSupabaseExt } from "../../lib/supabase";

// ───────────────────────── Config (same as current component) ─────────────────────────
const AGENCY_ID = 3;
const ORGANIZATION_ID = "3";
const CAP_MINUTES = 480; // working minutes per day (8h), used to turn a duration into an end date
// Outcome is captured in the UI but there is no API field for it yet.
// Flip to true if you want to force the user to fill it.
const OUTCOME_REQUIRED = false;

const PODS_WITH_AGENCY = ["client", "agency"];
const showAgencyForPod = (podName: string) =>
  PODS_WITH_AGENCY.some((p) => podName.toLowerCase().includes(p));

const TEMPLATES_WITH_SCREEN = ["defect", "enhancement"];
const showScreenForTemplate = (tpl: TemplateOption | null) =>
  !!tpl &&
  TEMPLATES_WITH_SCREEN.some((t) =>
    `${tpl.tag} ${tpl.name}`.toLowerCase().includes(t),
  );

const isAgentTask = (task: any) =>
  (task?.tag || "").toLowerCase().includes("agent") ||
  (task?.doer || "").toLowerCase().includes("agent");

const isAgentChecklistItem = (kind: string) =>
  kind === "agent_lint" || kind === "agent_selfcheck";

const templateTagsNotAllowed = ["bug-fix", "enhancements", "training-issue"];

// ───────────────────────── Types ─────────────────────────
interface Option {
  key: string;
  name: string;
}
interface TemplateOption {
  key: string;
  name: string;
  tag: string;
}
// pod = member of the selected pod, ext = any other employee (outside the pod)
type Backup = { kind: "pod" | "ext"; employee_id: string; name: string };

interface TaskState {
  employee_id: string; // primary
  employee_name: string;
  task_start_date: string; // "" = use the goal start date
  duration_hours: string;
  duration_minutes: string;
  note: string;
  backup: Backup | null;
  open: boolean; // description + checklist expanded
}

interface PickerState {
  taskKey: string;
  tab: "pod" | "ext";
  podSel: Option | null;
  extSel: Option | null;
  q: string; // search text
}

const emptyTaskState = (): TaskState => ({
  employee_id: "",
  employee_name: "",
  task_start_date: "",
  duration_hours: "",
  duration_minutes: "",
  note: "",
  backup: null,
  open: false,
});

// ───────────────────────── Date / schedule helpers ─────────────────────────
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
const parseISO = (s: string) => {
  const p = s.split("-");
  return new Date(+p[0], +p[1] - 1, +p[2], 12);
};
const addWork = (d: Date, n: number) => {
  const x = new Date(d);
  while (n > 0) {
    x.setDate(x.getDate() + 1);
    if (x.getDay() !== 0 && x.getDay() !== 6) n--;
  }
  return x;
};
const DN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MN = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const fmtDate = (d: Date) => `${DN[d.getDay()]} ${d.getDate()} ${MN[d.getMonth()]}`;
const fmtDur = (totalMin: number) => {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h ? `${h}h` : ""}${h && m ? " " : ""}${m ? `${m}m` : ""}` || "0m";
};
const initials = (n: string) =>
  n
    .split(/[\s+]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0].toUpperCase())
    .join("");
const taskMinutes = (s?: TaskState) =>
  (parseInt(s?.duration_hours || "0", 10) || 0) * 60 +
  (parseInt(s?.duration_minutes || "0", 10) || 0);
const hhmmss = (totalMin: number) =>
  `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(
    totalMin % 60,
  ).padStart(2, "0")}:00`;

interface Scheduled {
  start: Date;
  end: Date;
  endChunkMin: number; // minutes worked on the last day of the task
}

// Every task is independent: end = start + duration (working days, CAP_MINUTES a day).
// A task without a start date of its own uses the goal start date.
// Changing one task never moves another task.
const buildSchedule = (
  tasks: any[],
  states: { [k: string]: TaskState },
  goalStart: string | null,
) => {
  const out: { [k: string]: Scheduled } = {};
  tasks.forEach((t) => {
    const key = String(t.id);
    const st = states[key];
    const startStr = st?.task_start_date || goalStart;
    if (!startStr) return;
    const start = parseISO(startStr);
    if (Number.isNaN(start.getTime())) return;
    let end = start;
    let rem = taskMinutes(st);
    while (rem > CAP_MINUTES) {
      rem -= CAP_MINUTES;
      end = addWork(end, 1);
    }
    out[key] = { start, end, endChunkMin: rem };
  });
  return out;
};

// ───────────────────────── Component ─────────────────────────
const CreateGoalWizard: FunctionComponent = () => {
  const { userInfo, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as {
    projectID?: any;
    projectName?: string;
  };

  const employeeID = String(userInfo?.employee_id || "");
  const today = new Date().toISOString().split("T")[0];

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Goal fields ──
  const [projects, setProjects] = useState<Option[]>([]);
  const [selectedProject, setSelectedProject] = useState<Option | null>(
    routeState?.projectID
      ? {
          key: String(routeState.projectID),
          name: routeState.projectName || "",
        }
      : null,
  );
  const [allPods, setAllPods] = useState<any[]>([]);
  const [selectedPod, setSelectedPod] = useState<Option | null>(null);
  const [podTemplates, setPodTemplates] = useState<any[]>([]);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateOption | null>(null);
  const [goalTitle, setGoalTitle] = useState("");
  const [description, setDescription] = useState("");
  const [outcome, setOutcome] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [whatBuilding, setWhatBuilding] = useState("");

  const [extAgencies, setExtAgencies] = useState<Option[]>([]);
  const [extAgenciesLoading, setExtAgenciesLoading] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<Option | null>(null);

  const [screens, setScreens] = useState<Option[]>([]);
  const [selectedScreen, setSelectedScreen] = useState<Option | null>(null);

  // ── Tasks ──
  const [parsedTasks, setParsedTasks] = useState<any[]>([]);
  const [taskStates, setTaskStates] = useState<{ [k: string]: TaskState }>({});
  const [employees, setEmployees] = useState<Option[]>([]);

  // ── Backup picker ──
  const [picker, setPicker] = useState<PickerState | null>(null);
  // inline errors in step 3 show only after the user clicks "Create goal"
  const [tried, setTried] = useState(false);

  // ───────────────────────────── Queries ─────────────────────────────
  const { loading: employeesLoading } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
    fetchPolicy: "no-cache",
    variables: { agency_id: AGENCY_ID },
    onCompleted: (res: any) => {
      const list = res?.employees_by_agency_id?.employees || [];
      setEmployees(
        list
          .map((e: any) => ({
            key: String(e.employee_id),
            name: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
          }))
          .sort((a: Option, b: Option) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
      );
    },
  });

  const { loading: projectsLoading } = useQuery(PROJECTS_BY_EMP_ID, {
    fetchPolicy: "no-cache",
    variables: {
      emp_id: employeeID || "1000004",
      organization_id: ORGANIZATION_ID,
    },
    onCompleted: (data: any) => {
      setProjects(
        data?.projects_by_emp_id?.map((p: any) => ({
          key: String(p.id),
          name: p.name,
        })) || [],
      );
    },
  });

  const { loading: podsLoading } = useQuery(PODS_DATA_ATTENDANCE, {
    fetchPolicy: "no-cache",
    variables: { agency_id: AGENCY_ID, start_date: today, end_date: today },
    onCompleted: (res: any) => {
      const pods = res?.pods_data?.data || [];
      const visible = isAdmin
        ? pods
        : pods.filter((p: any) =>
            (p.members || []).some(
              (m: any) => String(m.employee_id) === employeeID,
            ),
          );
      setAllPods(visible?.filter((it: any) => it?.pod_name !== "All Pods"));
    },
  });

  const [getScreens, { data: screensData, loading: screensLoading }] =
    useLazyQuery(SCREENS_BY_PROJECT, { fetchPolicy: "no-cache" });

  const [getTemplates, { loading: templatesLoading }] = useLazyQuery(
    POD_GOAL_TEMPLATES,
    {
      fetchPolicy: "no-cache",
      onCompleted: (res: any) => {
        const list = res?.pod_goal_templates?.goal_templates || [];
        setPodTemplates(list);
        setTemplateOptions(
          list
            .map((t: any) => ({
              key: String(t.id),
              name: t.name,
              tag: t.tag || "",
            }))
            .filter((it: any) => !templateTagsNotAllowed?.includes(it?.tag)),
        );
        setSelectedTemplate(null);
      },
      onError: (e: ApolloError) => showError(e.message),
    },
  );

  const [createPodGoalCustom, { loading: createLoading }] = useMutation(
    CREATE_POD_GOAL_CUSTOM,
    { fetchPolicy: "no-cache" },
  );

  // ─────────────────────── Derived ───────────────────────
  const selectedPodData = allPods.find(
    (p: any) => String(p.pod_id) === selectedPod?.key,
  );
  const goalMembers: Option[] = (selectedPodData?.members || [])
    .map((m: any) => ({
      key: String(m.employee_id),
      name: `${m.first_name || ""} ${m.last_name || ""}`.trim(),
    }))
    .sort((a: Option, b: Option) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

  // pod members + anyone picked as primary who is outside the pod
  const finalGoalMembers: Option[] = (() => {
    const map = new Map<string, Option>();
    goalMembers.forEach((m) => map.set(m.key, m));
    Object.values(taskStates).forEach((a) => {
      if (a.employee_id && !map.has(a.employee_id)) {
        map.set(a.employee_id, { key: a.employee_id, name: a.employee_name });
      }
    });
    return Array.from(map.values());
  })();

  const showAgency = showAgencyForPod(selectedPod?.name || "");
  const showScreen = showScreenForTemplate(selectedTemplate);
  const isEnhancement =
    (selectedTemplate?.tag || "").toLowerCase() === "enhancement" ||
    (selectedTemplate?.name || "").toLowerCase() === "enhancement";
  const isDuplicateTask = (task: any) => Boolean(task?.isDuplicate);

  const schedule = useMemo(
    () => buildSchedule(parsedTasks, taskStates, startDate),
    [parsedTasks, taskStates, startDate],
  );

  const lastTaskKey = useMemo(() => {
    let lastKey: string | null = null;
    parsedTasks.forEach((t) => {
      const k = String(t.id);
      if (!schedule[k]) return;
      if (!lastKey || schedule[k].end >= schedule[lastKey].end) lastKey = k;
    });
    return lastKey;
  }, [parsedTasks, schedule]);

  const goalEnd: Date | null = lastTaskKey ? schedule[lastTaskKey].end : null;
  const totalMin = parsedTasks.reduce(
    (a, t) => a + taskMinutes(taskStates[String(t.id)]),
    0,
  );
  const checklistTotal = parsedTasks.reduce(
    (a, t) => a + (t.checklist_items?.length || 0),
    0,
  );
  const tasksMissingBackup = parsedTasks.filter(
    (t) => !taskStates[String(t.id)]?.backup,
  );
  const primaryCount = parsedTasks.filter(
    (t) => taskStates[String(t.id)]?.employee_id,
  ).length;

  // ─────────────────────── External agencies (supabase) ───────────────────────
  const fetchExtAgencies = useCallback(async () => {
    setExtAgenciesLoading(true);
    try {
      const { data, error } = await getSupabaseExt()
        .from("agency")
        .select("agency_id, name, status")
        .eq("status", "Active");
      if (!error && data) {
        setExtAgencies(
          data.map((row: any) => ({
            key: String(row.agency_id),
            name: row.name,
          })),
        );
      }
    } finally {
      setExtAgenciesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showAgency && extAgencies.length === 0) fetchExtAgencies();
    if (!showAgency) setSelectedAgency(null);
  }, [showAgency, extAgencies.length, fetchExtAgencies]);

  useEffect(() => {
    if (showScreen && selectedProject?.key) {
      getScreens({ variables: { project_id: Number(selectedProject.key) } });
    }
    if (!showScreen) setSelectedScreen(null);
  }, [showScreen, selectedProject?.key, getScreens]);

  useEffect(() => {
    if (screensData?.screens_by_project) {
      setScreens(
        screensData.screens_by_project.map((s: any) => ({
          key: String(s.id),
          name: s.name,
        })),
      );
    } else {
      setScreens([]);
    }
  }, [screensData]);

  // ───────────────────────── Pod → templates, Template → tasks ─────────────────────────
  const resetTasks = () => {
    setParsedTasks([]);
    setTaskStates({});
    setPicker(null);
    setTried(false);
  };

  const initTasks = (tasks: any[]) => {
    const states: { [k: string]: TaskState } = {};
    tasks.forEach((t) => {
      states[String(t.id)] = emptyTaskState();
    });
    setParsedTasks(tasks);
    setTaskStates(states);
  };

  useEffect(() => {
    setSelectedTemplate(null);
    setTemplateOptions([]);
    setPodTemplates([]);
    resetTasks();
    if (selectedPod?.key) {
      getTemplates({ variables: { pod_id: Number(selectedPod.key) } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPod?.key]);

  useEffect(() => {
    resetTasks();
    if (!selectedTemplate?.key) return;
    const tpl = podTemplates.find(
      (t: any) => String(t.id) === selectedTemplate.key,
    );
    const tasks = [...(tpl?.tasks || [])]
      .sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((t: any) => ({
        id: t.id,
        title: t.name,
        tag: t.tag,
        doer: t.doer,
        sequence: t.sequence,
        associated_goal_ids: t.associated_goal_ids,
        checklist_items: [...(t.checklists || [])]
          .sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0))
          .map((c: any) => ({
            id: c.id,
            kind: c.kind,
            checklist_name: c.name,
            status: c.status,
            ticked: false,
          })),
      }));
    initTasks(tasks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.key, podTemplates]);

  // Escape closes the backup popup
  useEffect(() => {
    if (!picker) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPicker(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [picker]);

  // ───────────────────────────── Handlers ─────────────────────────────
  const updateTask = (taskKey: string, patch: Partial<TaskState>) =>
    setTaskStates((prev) => ({
      ...prev,
      [taskKey]: { ...prev[taskKey], ...patch },
    }));

  const handleSelectPod = (key: string, name: string) => {
    if (key === selectedPod?.key) return;
    setSelectedPod({ key: String(key), name });
    setSelectedTemplate(null);
    setSelectedAgency(null);
  };

  const handleSelectTemplate = (key: string, name: string) => {
    const tpl = templateOptions.find((t) => t.key === String(key));
    setSelectedTemplate(
      tpl ? { key: String(key), name, tag: tpl.tag || "" } : null,
    );
  };

  const handleSelectProject = (key: string, name: string) => {
    setSelectedProject({ key: String(key), name });
    setSelectedScreen(null);
  };

  const handlePrimaryChange = (
    taskKey: string,
    empId: string,
    empName: string,
  ) => {
    setTaskStates((prev) => {
      const cur = prev[taskKey];
      // backup cannot be the same person as the primary
      const clearBackup = cur.backup?.employee_id === empId;
      return {
        ...prev,
        [taskKey]: {
          ...cur,
          employee_id: empId,
          employee_name: empName,
          backup: clearBackup ? null : cur.backup,
        },
      };
    });
  };

  const handleDuplicateTask = (sourceTask: any) => {
    const sourceKey = String(sourceTask.id);
    const duplicateId = `duplicate-${sourceKey}-${Date.now()}`;
    const duplicateTask = {
      ...sourceTask,
      id: duplicateId,
      isDuplicate: true,
      duplicateOf: sourceKey,
      checklist_items: (sourceTask.checklist_items || []).map((item: any) => ({
        ...item,
        ticked: false,
      })),
    };
    setParsedTasks((prev) => {
      let lastRelatedIndex = -1;
      prev.forEach((t: any, i: number) => {
        if (
          String(t.id) === sourceKey ||
          (t.isDuplicate && String(t.duplicateOf) === sourceKey)
        ) {
          lastRelatedIndex = i;
        }
      });
      if (lastRelatedIndex === -1) return prev;
      const next = [...prev];
      next.splice(lastRelatedIndex + 1, 0, duplicateTask);
      return next;
    });
    setTaskStates((prev) => ({ ...prev, [duplicateId]: emptyTaskState() }));
  };

  const handleRemoveDuplicateTask = (task: any) => {
    const id = String(task.id);
    setParsedTasks((prev) => prev.filter((t: any) => String(t.id) !== id));
    setTaskStates((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Hours 0–12, minutes 0–59, digits only
  const handleDurationChange = (
    taskKey: string,
    field: "duration_hours" | "duration_minutes",
    raw: string,
  ) => {
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    if (digitsOnly === "") return updateTask(taskKey, { [field]: "" });
    const num = parseInt(digitsOnly, 10);
    if (field === "duration_minutes" && num > 59) return;
    if (field === "duration_hours" && num > 12) return;
    updateTask(taskKey, { [field]: String(num) });
  };

  // ── Backup picker ──
  const openPicker = (taskKey: string) => {
    const b = taskStates[taskKey]?.backup;
    const cur = b ? { key: b.employee_id, name: b.name } : null;
    setPicker({
      taskKey,
      tab: b?.kind === "ext" ? "ext" : "pod",
      podSel: b?.kind === "pod" ? cur : null,
      extSel: b?.kind === "ext" ? cur : null,
      q: "",
    });
  };

  const saveBackup = () => {
    if (!picker) return;
    const sel = picker.tab === "pod" ? picker.podSel : picker.extSel;
    if (!sel?.key)
      return showError(
        picker.tab === "pod"
          ? "Please select a pod member"
          : "Please select a member",
      );
    // anyone who is not in the pod roster is saved as external
    const inPod = goalMembers.some((m) => m.key === sel.key);
    updateTask(picker.taskKey, {
      backup: {
        kind: inPod ? "pod" : "ext",
        employee_id: sel.key,
        name: sel.name,
      },
    });
    setPicker(null);
  };

  // ───────────────────────────── Step navigation ─────────────────────────────
  const goNextFromStep1 = () => {
    if (!selectedPod?.key) return showError("Please select a pod");
    if (!selectedTemplate?.key) return showError("Please select a template");
    if (parsedTasks.length < 1)
      return showError("No tasks found for the selected template");
    setStep(2);
    window.scrollTo(0, 0);
  };

  const goNextFromStep2 = () => {
    if (!selectedProject?.key) return showError("Please select a project");
    if (!goalTitle.trim()) return showError("Please enter a goal title");
    if (showAgency && !selectedAgency?.key)
      return showError("Please select an agency");
    if (!startDate) return showError("Please select a start date");
    if (isEnhancement && !whatBuilding.trim())
      return showError("Please enter the scope");
    if (OUTCOME_REQUIRED && !outcome.trim())
      return showError("Please enter the outcome");
    if (finalGoalMembers.length < 1)
      return showError("This pod has no members to add to the goal");
    setStep(3);
    window.scrollTo(0, 0);
  };

  // ───────────────────────────── Submit ─────────────────────────────
  const handleSubmit = async () => {
    setTried(true);
    // ── Goal level validation ──
    if (!selectedProject?.key) return showError("Please select a project");
    if (!selectedPod?.key) return showError("Please select a pod");
    if (!selectedTemplate?.key) return showError("Please select a template");
    if (!goalTitle.trim()) return showError("Please enter a goal title");
    if (showAgency && !selectedAgency?.key)
      return showError("Please select an agency");
    if (!startDate) return showError("Please select a start date");
    if (isEnhancement && !whatBuilding.trim())
      return showError("Please enter the scope");
    if (finalGoalMembers.length < 1)
      return showError("This pod has no members to add to the goal");
    if (parsedTasks.length < 1)
      return showError("No tasks found for the selected template");

    // ── Task level validation ──
    for (const task of parsedTasks) {
      const taskKey = String(task.id);
      const st = taskStates[taskKey];
      const sc = schedule[taskKey];

      if (!st?.employee_id)
        return showError(`Please assign a member to "${task.title}"`);
      if (!st.backup)
        return showError(`Please choose a backup for "${task.title}"`);
      if (st.backup.employee_id === st.employee_id)
        return showError(
          `Backup cannot be the same as the primary for "${task.title}"`,
        );
      if (!sc || Number.isNaN(sc.start.getTime()))
        return showError(`Please select valid dates for "${task.title}"`);
      if (iso(sc.start) < startDate)
        return showError(
          `Start date for "${task.title}" cannot be before the goal start date`,
        );
      const hours = Number(st.duration_hours || 0);
      const minutes = Number(st.duration_minutes || 0);
      if (!Number.isInteger(hours) || hours < 0 || hours > 12)
        return showError(`Please enter valid hours (0-12) for "${task.title}"`);
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59)
        return showError(
          `Please enter valid minutes (0-59) for "${task.title}"`,
        );
      if (hours === 0 && minutes === 0)
        return showError(`Please enter a duration for "${task.title}"`);
    }

    const buildBackupFields = (b: Backup) => ({
      // backup_type: b.kind === "pod" ? "pod" : "external",
      backup_assignee_id: parseInt(b.employee_id),
      // backup_name: b.name,
    });

    const buildTaskPayload = (task: any) => {
      const taskKey = String(task.id);
      const st = taskStates[taskKey];
      const sc = schedule[taskKey];
      return {
        tasks_static_id: isDuplicateTask(task)
          ? Number(task.duplicateOf)
          : Number(task.id),
        employee_id: parseInt(st.employee_id),
        start_datetime: `${iso(sc.start)}T00:00:00Z`,
        end_datetime: `${iso(sc.end)}T${hhmmss(sc.endChunkMin)}Z`,
        skill_ids: [] as number[],
        task_note: st.note ?? "",
        ...buildBackupFields(st.backup as Backup),
      };
    };

    const taskAssigneesPayload = parsedTasks
      .filter((t: any) => !isDuplicateTask(t))
      .map(buildTaskPayload);
    const duplicateTasksPayload = parsedTasks
      .filter((t: any) => isDuplicateTask(t))
      .map(buildTaskPayload);

    const variables = {
      input: {
        project_id: parseInt(selectedProject.key),
        pod_id: parseInt(selectedPod.key),
        template_tag: selectedTemplate.tag,
        goal_title: goalTitle.trim(),
        description,
        start_date: `${startDate}T00:00:00Z`,
        end_date: goalEnd ? `${iso(goalEnd)}T23:59:59Z` : null,
        skill_ids: [] as number[],
        goal_members: finalGoalMembers.map((m) => parseInt(m.key)),
        created_by: parseInt(employeeID),
        task_assignees: taskAssigneesPayload,
        duplicate_tasks: duplicateTasksPayload,
        // outcome: not sent, no API field yet
      },
    };

    try {
      const res = await createPodGoalCustom({ variables });
      const result = res.data?.create_pod_goal_custom;
      if (result?.status_code === 200 || result?.status_code === 201) {
        showSuccess(result?.message || "Goal created successfully");
        navigate(-1);
      } else {
        showError(result?.message || "Failed to create goal");
      }
    } catch (err) {
      if (err instanceof ApolloError) showError(err.message);
    }
  };

  // ───────────────────────────── Render helpers ─────────────────────────────
  const stepper = (
    <div className="panel stepper">
      {[
        [1, "Select template"],
        [2, "Goal details"],
        [3, "Assign tasks"],
      ].map(([i, label], idx) => {
        const n = i as number;
        const cls = step > n ? "done" : step === n ? "" : "todo";
        return (
          <span key={n} style={{ display: "contents" }}>
            {idx > 0 && <div className="line" />}
            <div className={`step ${cls}`}>
              <span className="dot">{step > n ? "✓" : n}</span>
              <span className="t">{label}</span>
            </div>
          </span>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <div className="panel body">
      <div className="grid2">
        <SearchableInputsDropdown
          label="Pod*"
          initialValue={selectedPod?.name || ""}
          placeHolder="Select pod"
          options={allPods.map((p: any) => ({
            key: String(p.pod_id),
            name: p.pod_name,
          }))}
          dontselectlastValue={true}
          onSelect={handleSelectPod}
        />
        <SearchableInputsDropdown
          key={`template-${selectedPod?.key || "none"}`}
          label="Template*"
          initialValue={selectedTemplate?.name || ""}
          placeHolder={selectedPod ? "Select template" : "Select pod first"}
          options={templateOptions}
          dontselectlastValue={true}
          onSelect={handleSelectTemplate}
        />
      </div>

      {selectedTemplate && parsedTasks.length > 0 && (
        <>
          <div className="info" style={{ marginTop: 22 }}>
            <b>
              {selectedTemplate.name} · {parsedTasks.length} tasks ·{" "}
              {checklistTotal} checklist items
            </b>
          </div>
          <div style={{ marginTop: 22 }}>
            <div className="trow thead">
              <span />
              <span>Tasks that will be created</span>
            </div>
            {parsedTasks.map((t: any, idx: number) => (
              <div className="trow" key={String(t.id)}>
                <span className="num">{idx + 1}</span>
                <span>{t.title}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {!selectedTemplate && (
        <p className="muted" style={{ marginTop: 22 }}>
          Select a pod and a template to see the tasks that will be created.
        </p>
      )}

      <div className="foot">
        <span className="muted" />
        <div className="r">
          <button className="btn ghost" type="button" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button className="btn pri" type="button" onClick={goNextFromStep1}>
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="panel body">
      <div className="pills">
        <span className="pill">{selectedPod?.name}</span>
        <span className="pill">{selectedTemplate?.name}</span>
      </div>

      <div className="grid2">
        <div>
          <label className="lbl big" htmlFor="wz-title">
            Goal title*
          </label>
          <input
            id="wz-title"
            className="inp"
            value={goalTitle}
            placeholder="Goal Title"
            onChange={(e) => setGoalTitle(e.target.value)}
          />
        </div>
        <SearchableInputsDropdown
          key={`project-${selectedProject?.key || "none"}`}
          label="Project*"
          initialValue={selectedProject?.name || ""}
          placeHolder="Select project"
          options={projects}
          dontselectlastValue={true}
          onSelect={handleSelectProject}
        />

        {showAgency && (
          <SearchableInputsDropdown
            label="Agency Name*"
            initialValue={selectedAgency?.name || ""}
            placeHolder="Select agency"
            options={extAgencies}
            dontselectlastValue={true}
            onSelect={(key: string, name: string) =>
              setSelectedAgency({ key: String(key), name })
            }
          />
        )}

        {showScreen && (
          <SearchableInputsDropdown
            key={`screen-${selectedProject?.key || "none"}`}
            label="Screen Name"
            initialValue={selectedScreen?.name || ""}
            placeHolder="Select screen"
            options={screens}
            dontselectlastValue={true}
            onSelect={(key: string, name: string) =>
              setSelectedScreen({ key: String(key), name })
            }
          />
        )}

        <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
          <DateSelector
            label="Start*"
            selectedDate={startDate}
            onChange={(date: string | null) => setStartDate(date)}
            nothideParsedValue={true}
          />
        </div>
        <div>
          <label className="lbl big">End · set by the last task in step 3</label>
          <div className="endbox big">
            {goalEnd ? fmtDate(goalEnd) : "—"}
            <span className="auto">AUTO</span>
          </div>
        </div>
      </div>

      <div className="wide" style={{ marginTop: 20 }}>
        <label className="lbl big" htmlFor="wz-desc">
          Description
        </label>
        <textarea
          id="wz-desc"
          className="inp"
          rows={4}
          placeholder="Goal Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {isEnhancement && (
        <div className="wide" style={{ marginTop: 20 }}>
          <label className="lbl big" htmlFor="wz-scope">
            The Scope* · what we are building, and what we are not
          </label>
          <textarea
            id="wz-scope"
            className="inp"
            rows={3}
            placeholder="The Scope"
            value={whatBuilding}
            onChange={(e) => setWhatBuilding(e.target.value)}
          />
        </div>
      )}

      <div className="wide" style={{ marginTop: 20 }}>
        <label className="lbl big" htmlFor="wz-out">
          Outcome{OUTCOME_REQUIRED ? "*" : ""} · what does done look like
        </label>
        <input
          id="wz-out"
          className="inp"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <span className="lbl big">
          Members · {selectedPod?.name || "pod"} members
        </span>
        {finalGoalMembers.length === 0 ? (
          <p className="muted">This pod has no members.</p>
        ) : (
          <div className="chips">
            {finalGoalMembers.map((m) => (
              <div className="chip" key={m.key}>
                <b>{m.name}</b>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="foot">
        <span />
        <div className="r">
          <button className="btn ghost" type="button" onClick={() => setStep(1)}>
            Back
          </button>
          <button className="btn pri" type="button" onClick={goNextFromStep2}>
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const renderBackupField = (taskKey: string, task: any, st: TaskState) => {
    const b = st.backup;
    if (!b) {
      return (
        <>
          <button
            type="button"
            className={`bk${tried ? " miss" : ""}`}
            aria-label={`Choose backup for ${task.title}`}
            onClick={() => openPicker(taskKey)}
          >
            <span className="muted">Choose a backup</span>
            <span aria-hidden>▾</span>
          </button>
          {tried && (
            <div className="errmsg">Backup required: pod member or external</div>
          )}
        </>
      );
    }
    const ext = b.kind === "ext";
    return (
      <button
        type="button"
        className="bk"
        aria-label={`Change backup for ${task.title}`}
        onClick={() => openPicker(taskKey)}
      >
        <span className="who">
          <span className={`av${ext ? " ex" : ""}`}>{initials(b.name)}</span>
          <span className="trunc">{b.name}</span>
          {ext && <span className="ext">External</span>}
        </span>
        <span aria-hidden>▾</span>
      </button>
    );
  };

  const renderTaskCard = (task: any, idx: number) => {
    const taskKey = String(task.id);
    const st = taskStates[taskKey] || emptyTaskState();
    const sc = schedule[taskKey];
    const isAgent = isAgentTask(task);
    const items: any[] = task.checklist_items || [];
    const humans = items.filter((i) => !isAgentChecklistItem(i.kind)).length;

    return (
      <div
        className={`task${tried && (!st.employee_id || !st.backup) ? " err" : ""}`}
        key={taskKey}
      >
        <div className="thd">
          <span className="num">{idx + 1}</span>
          <span className="t">{task.title}</span>
          {isAgent && <span className="tag a">🤖 Agent Slot</span>}
          {lastTaskKey === taskKey && (
            <span className="tag last">Last to finish · sets goal end</span>
          )}
          <span style={{ marginLeft: "auto" }}>
            {isDuplicateTask(task) ? (
              <button
                type="button"
                className="iconbtn rm"
                aria-label="Remove duplicated task"
                title="Remove duplicated task"
                onClick={() => handleRemoveDuplicateTask(task)}
              >
                ✕
              </button>
            ) : (
              <button
                type="button"
                className="iconbtn add"
                aria-label={`Duplicate task ${task.title}`}
                title="Duplicate task"
                onClick={() => handleDuplicateTask(task)}
              >
                +
              </button>
            )}
          </span>
        </div>

        <div className="fields">
          <div>
            <SearchableInputsDropdown
              key={`assign-${taskKey}-${selectedPod?.key || ""}`}
              label="Primary"
              initialValue={st.employee_name || ""}
              placeHolder="Select member"
              options={employees}
              dontselectlastValue={true}
              onSelect={(key: string, name: string) =>
                handlePrimaryChange(taskKey, key, name)
              }
            />
            {tried && !st.employee_id && (
              <div className="errmsg">Primary required</div>
            )}
          </div>
          <div>
            <span className="lbl">Backup</span>
            {renderBackupField(taskKey, task, st)}
          </div>
          <div>
            <DateSelector
              key={`start-${taskKey}`}
              label="Start"
              selectedDate={st.task_start_date || startDate || null}
              onChange={(date: string | null) =>
                updateTask(taskKey, { task_start_date: date || "" })
              }
              minDate={startDate ? new Date(startDate) : undefined}
              nothideParsedValue={true}
            />
          </div>
          <div>
            <span className="lbl">Duration</span>
            <div className="dur">
              <input
                className="inp"
                type="text"
                inputMode="numeric"
                maxLength={2}
                placeholder="HH"
                aria-label="Hours"
                value={st.duration_hours}
                onChange={(e) =>
                  handleDurationChange(taskKey, "duration_hours", e.target.value)
                }
              />
              <small>h</small>
              <input
                className="inp"
                type="text"
                inputMode="numeric"
                maxLength={2}
                placeholder="MM"
                aria-label="Minutes"
                value={st.duration_minutes}
                onChange={(e) =>
                  handleDurationChange(
                    taskKey,
                    "duration_minutes",
                    e.target.value,
                  )
                }
              />
              <small>m</small>
            </div>
          </div>
          <div>
            <span className="lbl">End</span>
            <div className="endbox">
              {sc && taskMinutes(st) > 0 ? fmtDate(sc.end) : "—"}
              <span className="auto">AUTO</span>
            </div>
          </div>
        </div>

        <div className="more">
          <button
            type="button"
            className="tog"
            aria-expanded={st.open}
            onClick={() => updateTask(taskKey, { open: !st.open })}
          >
            <span className="chev" aria-hidden>
              ▸
            </span>
            Description and checklist · {items.length} items ({humans} Human,{" "}
            {items.length - humans} Agent)
          </button>
          {st.open && (
            <div>
              <label className="lbl" style={{ marginTop: 14 }} htmlFor={`d-${taskKey}`}>
                Task description
              </label>
              <textarea
                id={`d-${taskKey}`}
                className="inp"
                rows={2}
                placeholder="Add a description for this task..."
                value={st.note}
                onChange={(e) => updateTask(taskKey, { note: e.target.value })}
              />
              {items.length > 0 && (
                <>
                  <span className="lbl" style={{ marginTop: 14 }}>
                    Checklist
                  </span>
                  {items.map((item, cidx) => {
                    const agent = isAgentChecklistItem(item.kind);
                    return (
                      <div className="ck" key={item.id ?? cidx}>
                        <span className={`tag ${agent ? "a" : "h"}`}>
                          {agent ? "Agent" : "Human"}
                        </span>
                        {item.checklist_name}
                        {agent && (
                          <i className="muted"> (agent verifies)</i>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const firstMissing = tasksMissingBackup[0];
    const missCount = tasksMissingBackup.length;
    const total = parsedTasks.length;
    return (
      <>
        <div className="panel sumbar">
          <div className="goal">
            <small>GOAL</small>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>
              {goalTitle}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              {selectedPod?.name} · {selectedTemplate?.name}
            </div>
          </div>
          <div className="stat">
            <span>Start</span>
            <b>{startDate ? fmtDate(parseISO(startDate)) : "—"}</b>
          </div>
          <div className="stat end">
            <span>Goal ends · last task to finish</span>
            <b>{goalEnd ? fmtDate(goalEnd) : "—"}</b>
          </div>
          <div className="stat">
            <span>Primary</span>
            <b>
              {primaryCount} / {total}
            </b>
          </div>
          <div className="stat">
            <span>Backup</span>
            <b
              style={{
                color: missCount
                  ? tried
                    ? "var(--red)"
                    : "inherit"
                  : "#23683a",
              }}
            >
              {total - missCount} / {total}
            </b>
          </div>
          <div className="stat">
            <span>Effort</span>
            <b>{fmtDur(totalMin)}</b>
          </div>
        </div>

        {/* <div className="hint">
          Every task needs a primary and a backup. Each task has its own start
          date and duration, its end date is start + duration, and the goal end
          date is the latest task end.
        </div> */}

        {parsedTasks.map(renderTaskCard)}

        <div className="panel bar">
          <span className="msg">
            {tried && missCount > 0
              ? `Task ${parsedTasks.indexOf(firstMissing) + 1} ${
                  firstMissing.title
                } needs a backup${
                  missCount > 1 ? ` (and ${missCount - 1} more)` : ""
                } before the goal can be created.`
              : ""}
          </span>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn ghost" type="button" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              className="btn pri"
              type="button"
              onClick={handleSubmit}
              disabled={
                createLoading || templatesLoading || total === 0
              }
            >
              Create goal
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderPicker = () => {
    if (!picker) return null;
    const idx = parsedTasks.findIndex((t) => String(t.id) === picker.taskKey);
    const task = parsedTasks[idx];
    const st = taskStates[picker.taskKey];
    if (!task || !st) return null;

    const notPrimary = (m: Option) => m.key !== st.employee_id;
    const podIds = new Set(goalMembers.map((m) => m.key));
    // Pod Members tab: only the pod members
    const podOptions = goalMembers.filter(notPrimary);
    // External tab: members outside the pod first, then the pod members
    const extOptions = [
      ...employees.filter((e) => !podIds.has(e.key)),
      ...goalMembers,
    ].filter(notPrimary);
    const needle = picker.q.trim().toLowerCase();
    const list = (picker.tab === "pod" ? podOptions : extOptions).filter((m) =>
      m.name.toLowerCase().includes(needle),
    );
    const selKey = (picker.tab === "pod" ? picker.podSel : picker.extSel)?.key;

    return (
      <div
        className="scrim"
        onClick={(e) => {
          if (e.target === e.currentTarget) setPicker(null);
        }}
      >
        <div className="modal" role="dialog" aria-modal="true" aria-labelledby="wz-mt">
          <div className="mhd">
            <div id="wz-mt" style={{ fontSize: 20, fontWeight: 700 }}>
              Choose a backup
            </div>
            <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
              Task {idx + 1} · {task.title} · primary is{" "}
              {st.employee_name || "not selected"}
            </div>
            <div className="tabs" role="tablist">
              {(["pod", "ext"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  className="tab"
                  aria-selected={picker.tab === t}
                  onClick={() => setPicker({ ...picker, tab: t, q: "" })}
                >
                  {t === "pod" ? "Pod members" : "External"}
                </button>
              ))}
            </div>
          </div>

          <div className="mbody">
            <input
              className="inp"
              type="text"
              autoFocus
              placeholder="Search by name"
              aria-label="Search members"
              value={picker.q}
              onChange={(e) => setPicker({ ...picker, q: e.target.value })}
            />
            <div className="plist">
              {list.length === 0 && (
                <p className="muted" style={{ margin: 0 }}>
                  No members found.
                </p>
              )}
              {list.map((m) => {
                const sel = selKey === m.key;
                const inPod = podIds.has(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    className={`opt${sel ? " sel" : ""}`}
                    onClick={() =>
                      setPicker({
                        ...picker,
                        ...(picker.tab === "pod"
                          ? { podSel: { key: m.key, name: m.name } }
                          : { extSel: { key: m.key, name: m.name } }),
                      })
                    }
                  >
                    <span className="av big">{initials(m.name)}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{m.name}</span>
                    {picker.tab === "ext" && inPod && (
                      <span className="tagp">Pod</span>
                    )}
                    {sel && (
                      <span style={{ color: "var(--pur)", fontWeight: 700 }}>
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mfoot">
            <button className="btn ghost" type="button" onClick={() => setPicker(null)}>
              Cancel
            </button>
            <button className="btn pri" type="button" onClick={saveBackup}>
              Set backup
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ───────────────────────────── Render ─────────────────────────────
  return (
    <>
      <style>{WIZARD_CSS}</style>
      <div className="wzk">
        <div className="wrap">
          <BreadCumb clientName={selectedProject?.name || ""} />
          <div className="titlerow">
            <img
              className="backimg dark-white-img"
              alt="Back"
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h1>Create goal</h1>
          </div>
          {stepper}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
        {renderPicker()}
      </div>

      {(projectsLoading ||
        podsLoading ||
        screensLoading ||
        extAgenciesLoading ||
        templatesLoading ||
        createLoading ||
        employeesLoading) && <Loader />}
    </>
  );
};

// Scoped under .wzk using native CSS nesting (Chrome 120+, Safari 17.2+, Firefox 117+).
const WIZARD_CSS = `
.wzk{
  --ink:#1f2430;--mute:#5f6676;--line:#e6e8f0;--line2:#d6d8e3;--bg:#f5f6fa;
  --pur:#5b3db5;--pur-d:#4a2fa3;--pur-t:#f0eafa;--grn:#3f9a62;--red:#b3261e;--blue-t:#eaf1fb;
  font-family:inherit;color:var(--ink);background:var(--bg);min-height:100vh;
  & *{box-sizing:border-box}
  & button,& input,& select,& textarea{font-family:inherit;font-size:15px;color:var(--ink)}
  & .wrap{max-width:1200px;margin:0 auto;padding:28px 32px 56px}
  & .titlerow{display:flex;align-items:center;gap:6px;margin:10px 0 18px}
  & .backimg{width:24px;height:24px;cursor:pointer}
  & h1{margin:0;font-size:26px;font-weight:700}
  & .muted{color:var(--mute)}
  & .trunc{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  & .panel{background:#fff;border:1px solid var(--line);border-radius:14px}
  & .stepper{display:flex;align-items:center;padding:18px 28px;margin-bottom:18px}
  & .step{display:flex;align-items:center;gap:12px;font-size:16px;font-weight:500;color:var(--pur-d);white-space:nowrap}
  & .step.todo{color:var(--mute)}
  & .dot{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;flex:none;background:var(--pur);color:#fff}
  & .step.done .dot{background:var(--grn)}
  & .step.todo .dot{background:#e8e9ef;color:var(--mute)}
  & .line{flex:1;height:1px;background:var(--line2);margin:0 20px}
  & .body{padding:28px}
  & .lbl{display:block;font-size:12px;font-weight:600;color:var(--mute);text-transform:uppercase;letter-spacing:.03em;margin-bottom:6px}
  & .lbl.big{font-size:13px;text-transform:none;letter-spacing:0;margin-bottom:8px}
  & .inp{width:100%;min-height:46px;border:1px solid var(--line2);border-radius:10px;background:#fff;padding:0 14px}
  & textarea.inp{padding:12px 14px;line-height:1.5;resize:vertical;min-height:80px}
  & .inp.bad{border-color:var(--red)}
  & .inp:disabled{background:#f1f2f7;color:#8a90a0;cursor:not-allowed}
  & .inp:focus,& button:focus-visible,& select:focus-visible{outline:3px solid #cdbff0;outline-offset:1px}
  & .grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px 24px;max-width:960px}
  & .wide{max-width:960px}
  & .pills{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:22px}
  & .pill{display:inline-block;border-radius:999px;padding:5px 12px;font-weight:600;font-size:14px;background:var(--blue-t);color:#25508f}
  & .btn{height:44px;padding:0 24px;border-radius:10px;font-weight:600;border:0;cursor:pointer}
  & .btn.pri{background:var(--pur);color:#fff}
  & .btn.pri:hover:not(:disabled){background:var(--pur-d)}
  & .btn.ghost{background:transparent;color:#4b5365}
  & .btn:disabled{background:#d9d2ef;color:#4b3a8a;cursor:not-allowed}
  & .foot{display:flex;align-items:center;justify-content:space-between;gap:12px;border-top:1px solid #eceef4;margin-top:28px;padding-top:20px}
  & .foot .r{display:flex;gap:12px;align-items:center;margin-left:auto}
  & .info{background:var(--blue-t);border:1px solid #d3e2f6;border-radius:12px;padding:16px 20px}
  & .info b{font-size:16px;font-weight:600}
  & .trow{display:grid;grid-template-columns:36px minmax(0,1fr);gap:16px;align-items:center;padding:13px 0;border-top:1px solid #eceef4;font-size:15px}
  & .thead{font-size:12px;font-weight:600;color:var(--mute);text-transform:uppercase;letter-spacing:.04em;border-top:0;padding-bottom:8px}
  & .num{width:30px;height:30px;border-radius:50%;background:var(--pur);color:#fff;font-size:13px;font-weight:600;display:flex;align-items:center;justify-content:center;flex:none}
  & .chips{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:14px;max-width:960px}
  & .chip{border-radius:12px;background:var(--pur-t);border:1px solid #e1d6f4;padding:14px 18px}
  & .chip b{display:block;font-size:16px;font-weight:600}
  & .sumbar{display:flex;align-items:stretch;padding:16px 8px;margin-bottom:14px;flex-wrap:wrap;gap:8px 0}
  & .sumbar .goal{flex:1 1 280px;padding:0 24px;min-width:0}
  & .sumbar .goal small{font-size:12px;font-weight:600;color:var(--mute)}
  & .stat{display:flex;flex-direction:column;gap:2px;padding:0 24px;border-left:1px solid var(--line);justify-content:center}
  & .stat span{font-size:12px;color:var(--mute);font-weight:600;text-transform:uppercase;letter-spacing:.03em}
  & .stat b{font-size:20px;font-weight:700}
  & .stat.end{background:var(--pur-t);border-radius:10px;border-left:0;padding:10px 20px;margin:0 12px}
  & .stat.end span{color:var(--pur-d)}
  & .stat.end b{color:#3d2a85}
  & .hint{font-size:14px;color:#4b5365;padding:0 4px;margin-bottom:14px}
  & .task{background:#fff;border:1px solid var(--line);border-radius:14px;padding:18px 24px;margin-bottom:14px}
  & .task.err{border-color:#e9b4b0}
  & .thd{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px}
  & .thd .t{font-size:16px;font-weight:600}
  & .tag{font-size:12px;font-weight:600;border-radius:6px;padding:3px 9px;flex:none}
  & .tag.last{background:#e1d6f4;color:#3d2a85}
  & .tag.h{background:#fdf0e0;color:#8a4b0a}
  & .tag.a{background:#e6f4ea;color:#23683a}
  & .iconbtn{width:36px;height:36px;border-radius:8px;border:0;cursor:pointer;font-size:18px;font-weight:700;line-height:1}
  & .iconbtn.add{background:var(--pur);color:#fff}
  & .iconbtn.rm{background:#fbeceb;color:var(--red)}
  & .fields{display:grid;grid-template-columns:minmax(0,1.25fr) minmax(0,1.25fr) 160px 130px 150px;gap:16px;align-items:start}
  & .dur{display:flex;gap:6px;align-items:center}
  & .dur input{width:54px;min-height:46px;text-align:center;padding:0 4px}
  & .dur small{color:var(--mute);font-size:13px}
  & .endbox{min-height:46px;border-radius:10px;background:#f1f2f7;color:#4b5365;display:flex;align-items:center;justify-content:space-between;padding:0 12px;gap:8px;font-size:15px}
  & .endbox.big{font-size:16px}
  & .auto{font-size:11px;font-weight:700;background:#e3e5ee;border-radius:6px;padding:2px 7px}
  & .bk{width:100%;min-height:46px;border:1px solid var(--line2);border-radius:10px;background:#fff;padding:0 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;text-align:left}
  & .bk:hover{border-color:var(--pur)}
  & .bk.miss{border-color:var(--red);background:#fff8f7;color:var(--mute)}
  & .bk .who{display:flex;align-items:center;gap:10px;min-width:0}
  & .av{width:26px;height:26px;border-radius:50%;background:#e1d6f4;color:#3d2a85;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex:none}
  & .av.big{width:34px;height:34px;font-size:12px}
  & .av.ex{background:#fdecc8;color:#7a4a00}
  & .ext{font-size:11px;font-weight:700;color:#7a4a00;background:#fdecc8;border-radius:6px;padding:2px 7px;text-transform:uppercase}
  & .errmsg{font-size:13px;color:var(--red);margin-top:6px;font-weight:500}
  & .more{border-top:1px solid #eceef4;margin-top:16px;padding-top:12px}
  & .tog{background:none;border:0;color:var(--pur-d);font-weight:500;font-size:14px;display:flex;align-items:center;gap:8px;cursor:pointer;padding:0}
  & .tog .chev{display:inline-block;transition:transform .15s}
  & .tog[aria-expanded=true] .chev{transform:rotate(90deg)}
  & .ck{display:flex;align-items:center;gap:10px;border:1px solid var(--line);border-radius:10px;padding:9px 12px;font-size:14px;margin-top:8px}
  & .bar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:18px 28px;flex-wrap:wrap}
  & .bar .msg{font-size:14px;color:var(--red);font-weight:500}
  & .bar .msg.ok{color:#23683a}
  & .scrim{position:fixed;inset:0;background:rgba(31,36,48,.55);display:flex;align-items:center;justify-content:center;padding:20px;z-index:1000}
  & .modal{background:#fff;border-radius:16px;width:560px;max-width:100%;max-height:92vh;display:flex;flex-direction:column;overflow:hidden}
  & .mhd{padding:24px 28px 0;border-bottom:1px solid #eceef4}
  & .tabs{display:flex;gap:4px;margin-top:14px}
  & .tab{background:none;border:0;border-bottom:3px solid transparent;padding:10px 16px;font-weight:600;color:var(--mute);cursor:pointer}
  & .tab[aria-selected=true]{color:var(--pur-d);border-bottom-color:var(--pur)}
  & .mbody{padding:20px 28px;overflow:hidden;display:flex;flex-direction:column;gap:12px}
  & .plist{max-height:340px;min-height:120px;overflow:auto;display:flex;flex-direction:column;gap:8px}
  & .tagp{font-size:11px;font-weight:700;color:var(--mute);background:#f1f2f7;border-radius:6px;padding:2px 7px;text-transform:uppercase}
  & .opt{display:flex;align-items:center;gap:12px;padding:12px 14px;border:1px solid var(--line);border-radius:12px;background:#fff;width:100%;text-align:left;cursor:pointer}
  & .opt:hover:not(:disabled){border-color:var(--pur)}
  & .opt.sel{border-color:var(--pur);background:var(--pur-t)}
  & .opt:disabled{opacity:.55;cursor:not-allowed}
  & .mfoot{padding:16px 28px;border-top:1px solid #eceef4;display:flex;justify-content:flex-end;gap:12px}
  @media(max-width:980px){
    & .fields{grid-template-columns:1fr 1fr}
    & .grid2{grid-template-columns:1fr}
    & .step .t{display:none}
    & .wrap{padding:20px 16px 40px}
  }
}
`;

export default CreateGoalWizard;